/**
 * App-wide, consent-gated analytics. Google Analytics, Google Tag Manager, the Facebook Pixel and
 * the Reddit Pixel load at RUNTIME only after the owner has accepted marketing cookies — never on page load. The
 * consent gate itself lives in `src/components/analytics-consent`, which calls into this module;
 * the measurement / pixel ids come from `window.config` (baked in `ini.js`, templated by the Helm
 * configmap per environment). A missing id disables that tracker.
 */
import { loadGoogleAnalytics, setGaEnabled, gaPageView } from './googleAnalytics';
import { loadFacebookPixel, fbSetConsent, fbPageView } from './facebookPixel';
import { loadRedditPixel, rdtSetConsent, rdtPageView } from './redditPixel';
import { loadGoogleTagManager, gtmSetConsent } from './googleTagManager';
import { scrubAnalyticsUrl, trackerSafeUrl } from './sanitize';

// Standardized FB ⇄ GA4 ⇄ Reddit conversion events (Lead / begin_checkout / purchase / …).
export * from './conversions';

const gaId = (): string => window.config?.googleAnalyticsId ?? '';
const pixelId = (): string => window.config?.facebookPixelId ?? '';
const redditId = (): string => window.config?.redditPixelId ?? '';
const gtmId = (): string => window.config?.gtmId ?? '';

/** Never track for the prerender bot (skews analytics; storage is disabled for it anyway). */
const skip = (): boolean => Boolean(window.config?.inPrerender);

/**
 * Apply the current marketing-consent decision. Idempotent and safe to call on every change:
 * when granted, each tracker is (lazily) loaded and enabled; when withdrawn, collection is
 * stopped (GA4 can't fully unload, so its disable flag is set and the Pixel's consent is
 * revoked — turning consent back on resumes both without a page reload).
 */
export function applyMarketingConsent(granted: boolean): void {
  if (skip()) return;
  const ga = gaId();
  const px = pixelId();
  const reddit = redditId();
  const gtm = gtmId();
  if (granted) {
    if (ga) {
      loadGoogleAnalytics(ga);
      setGaEnabled(ga, true);
    }
    // fbevents auto-attaches the full live URL to every event (its automatic click/metadata
    // capture included) with no override, so DEFER loading the Pixel while a sensitive param
    // (e.g. the magic-link token) is in the URL. The consent effect re-runs on route change,
    // so the Pixel loads on the first clean URL. GA is safe to load: its auto-collected
    // page_location is pinned to a scrubbed value via gtag('set').
    if (px && trackerSafeUrl()) {
      loadFacebookPixel(px);
      fbSetConsent(true);
    }
    // Reddit's PageVisit / events carry the referring URL with no override either, so DEFER the
    // Pixel the same way — it loads on the first clean URL when the consent effect re-runs.
    if (reddit && trackerSafeUrl()) {
      loadRedditPixel(reddit);
      rdtSetConsent(true);
    }
    // The container's tags read the live URL themselves, so DEFER GTM like the Pixels. It is
    // never unloaded; gtmSetConsent forwards each later decision to its tags (Consent Mode).
    if (gtm && trackerSafeUrl()) {
      gtmSetConsent(true);
      loadGoogleTagManager(gtm);
    }
    // The trackers that loaded on a clean page pause while the URL is not (Stats > Website keeps the owner's filters in
    // its query), and the next clean route grants them again (above). See `trackerSafeUrl`.
    if (!trackerSafeUrl()) {
      fbSetConsent(false);
      rdtSetConsent(false);
      gtmSetConsent(false);
    }
  } else {
    if (ga) setGaEnabled(ga, false);
    fbSetConsent(false); // guarded internally — no-ops if the Pixel never loaded
    rdtSetConsent(false); // in-module gate — no-ops all further Reddit calls
    gtmSetConsent(false); // pushes nothing if GTM never loaded
  }
}

/**
 * Record an SPA page view across whichever trackers are loaded. The caller (analytics-consent)
 * only invokes this while consent is granted, so it needs no consent check of its own.
 */
export function trackPageView(path: string): void {
  if (skip()) return;
  gaPageView(gaId(), scrubAnalyticsUrl(path));
  // fbq / rdt snapshot window.location themselves and offer no URL override, so a view on a URL
  // still carrying a sensitive param (e.g. the magic-link token), or on Stats > Website, is skipped rather than leaked.
  if (trackerSafeUrl()) {
    fbPageView();
    rdtPageView();
  }
}
